import { ArrowLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import OtpCodeInput from './OtpCodeInput';
import styles from './styles.module.scss';

// "4:32", not "272s" — matches the mm:ss format users already expect
// from any OTP/session countdown.
const formatCountdown = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// HORO-10509: the code-entry half of ResetPasswordStep, split out so the
// new-password screen (ResetPasswordNewPasswordStep) isn't shown until
// this one's done. "Continue" dispatches a real check (authSlice's
// emailVerifyResetCode, POST /auth/verify-reset-code) — a wrong/expired
// code rejects right here, via the same `error` display every other
// screen in this flow uses, and the screen doesn't advance.
//
// OtpCodeInput itself already handles the "paste the whole code at once"
// case and hints iOS Safari's QuickType bar to offer a code found in
// Mail via `autoComplete="one-time-code"` on each box (see its own
// comment) — reused as-is here, not reimplemented.
const ResetPasswordCodeStep = ({
    email,
    code,
    setCode,
    loading,
    error,
    isCodeComplete,
    isCodeExpired,
    remainingSec,
    resendCooldown,
    onResend,
    onContinue,
    onBack
}) => {
    const { t } = useTranslation();

    const onSubmit = (event) => {
        event.preventDefault();
        onContinue();
    };

    return (
        <>
            {/* Same header row as the other auth screens — back returns to
                the email-entry step (the immediately prior step in this
                wizard), not "sign-in". */}
            <div className={styles.signInHeader}>
                <button type="button" className={styles.backIconBtn} aria-label={t('buttons.back')} onClick={onBack}>
                    <ArrowLeft size={24} color="#FFFFFF" />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>{t('modal.resetPasswordCodeTitle')}</h2>
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            <p className={styles.subtitle}>{t('modal.verifyEmailLabel', { email })}</p>
            <form onSubmit={onSubmit}>
                <OtpCodeInput value={code} onChange={setCode} disabled={loading} />
                {/* `remainingSec === null` means codeExpiresAt hasn't
                    arrived yet (e.g. the backend response was somehow
                    missing `expiresInSec`) — shows nothing rather than a
                    misleading "0:00" for a code that may still be valid. */}
                {remainingSec !== null && (
                    <p className={styles.fieldHelperText}>
                        {isCodeExpired ? t('modal.codeExpired') : t('modal.codeExpiresIn', { time: formatCountdown(remainingSec) })}
                    </p>
                )}
                {error && <p className={styles.formError}>{error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.continue')}
                        loading={loading}
                        disable={loading || !isCodeComplete || isCodeExpired}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
            <p className={styles.helperText}>
                {t('modal.resendCode')}{' '}
                <button type="button" className={styles.helperTextAction} onClick={onResend} disabled={resendCooldown > 0}>
                    {resendCooldown > 0 ? t('modal.resendCodeThrottled', { seconds: resendCooldown }) : t('modal.resendCodeAction')}
                </button>
            </p>
        </>
    );
};

export default ResetPasswordCodeStep;
